"use client";

import { ShieldAlert } from "lucide-react";
import Link from "next/link";

import { cn } from "@/lib/utils";

import { SiteHeader } from "@/components/shell/site-header";

import { ProgressSweep } from "@/features/auth/components/admin-login/admin-login-shared";
import { CheckRow, FieldError } from "@/features/auth/components/auth-fields";
import {
  SIGNUP_HAVE_ACCOUNT,
  SIGNUP_LABELS,
  SIGNUP_LOGIN_LINK,
  SIGNUP_NEWSLETTER_LABEL,
  SIGNUP_NOTICE,
  SIGNUP_SUBMIT,
  SIGNUP_SUBMITTING,
  SIGNUP_TERMS_LABEL,
  SIGNUP_TITLE,
  type SignupScenario,
} from "@/features/auth/components/signup/signup-content";
import { useSignupPage } from "@/features/auth/components/signup/signup-model";
import {
  CaptchaImage,
  CountryOptions,
} from "@/features/auth/components/signup/signup-shared";

/**
 * `/signup` — the account creation screen.
 *
 * Promoted from the four-way design bake-off ("Split showcase", Railway) once it
 * was approved; B, C and D and the `?variant=` harness are gone. It replaced an
 * `AuthCard` + `SignupForm` page that had reworded every label, dropped the IP
 * notice and the Terms checkbox, and hid the security code whenever the site
 * config said captcha was off.
 *
 * The site header sits above a two-column full-height split, the same shape as
 * the approved `/admin/login` scaled up for a nine-field form: a plum brand panel
 * on the left carrying an oversized "Create an Account" lockup, the IP notice as
 * an inset warning card, and the "Log In" line pinned to the bottom — with the
 * form filling the right half. Both halves reach the viewport edges, so there is
 * no dead space at any width, and the notice reads as a standing site rule
 * rather than an alert about something the visitor just did.
 *
 * Field grouping is by shape: the two names share a row, the two passwords share
 * a row, everything else runs full width, and the security code sits beside its
 * challenge. Under `lg` the brand panel folds into a header strip above the form.
 */
export function SignupView({ scenario }: { scenario: SignupScenario }) {
  const {
    form,
    submitting,
    formError,
    captchaImage,
    refreshCaptcha,
    onSubmit,
  } = useSignupPage(scenario);
  const { register, formState } = form;
  const errors = formState.errors;

  const well =
    "h-11 w-full rounded-xl border border-input bg-elevated px-3.5 text-[15px] text-foreground outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground hover:border-border focus:border-primary focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.22)] disabled:opacity-40 disabled:hover:border-input";
  const labelClass = "mb-2 block text-[13px] font-medium text-muted-foreground";

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background font-sans text-foreground">
      <SiteHeader />

      <div className="grid flex-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        {/* Brand panel */}
        <aside
          className="relative flex flex-col overflow-hidden px-8 py-10 lg:px-14 lg:py-14"
          style={{
            background:
              "hsl(var(--card))",
          }}
        >
          {/* No wordmark here: the site header above already carries it, and two
           * XGODO lockups a hundred pixels apart read as a mistake. `my-auto`
           * centres this block in the panel and pushes the "Log In" line to the
           * bottom. */}
          <div className="my-auto">
            {/* `max-w-[11ch]` wraps the heading after "Create an" at `lg` without
             * splitting the string into two nodes — the copy stays one shared
             * constant. */}
            <h1 className="text-3xl font-bold tracking-tight lg:max-w-[11ch] lg:text-[2.75rem] lg:leading-[1.05]">
              {SIGNUP_TITLE}
            </h1>
            <div className="mt-6 hidden h-px w-16 bg-primary lg:block" />

            {/* The notice, as a standing rule rather than an error banner. */}
            <div className="mt-7 flex max-w-md gap-3 rounded-2xl border border-warning/25 bg-warning/10 p-4 lg:mt-9">
              <ShieldAlert
                aria-hidden="true"
                className="mt-0.5 size-[18px] shrink-0 text-warning-text"
              />
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                {SIGNUP_NOTICE}
              </p>
            </div>
          </div>

          <p className="mt-10 text-sm text-muted-foreground">
            {SIGNUP_HAVE_ACCOUNT}{" "}
            <Link
              href="/login"
              className="font-medium text-primary-text underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              {SIGNUP_LOGIN_LINK}
            </Link>
          </p>
        </aside>

        {/* Form panel */}
        <main
          id="main-content"
          className="relative grid place-items-center overflow-hidden px-6 py-12 lg:px-14 lg:py-14"
        >
          {/* The brand panel's light carried across the seam, weaker, so the two
           * halves read as one field rather than two panels. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 hidden w-px lg:block"
            style={{
              background:
                "hsl(var(--hairline))",
            }}
          />

          <div className="relative mx-auto w-full max-w-[34rem]">
            {formError ? (
              <p
                role="alert"
                className="mb-6 rounded-xl border border-destructive/25 bg-destructive/10 px-3.5 py-3 text-sm text-destructive-text"
              >
                {formError}
              </p>
            ) : null}

            <form onSubmit={onSubmit} noValidate className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="a-fname" className={labelClass}>
                    {SIGNUP_LABELS.fname}
                  </label>
                  <input
                    id="a-fname"
                    autoComplete="given-name"
                    disabled={submitting}
                    className={well}
                    {...register("fname")}
                  />
                  <FieldError message={errors.fname?.message} />
                </div>
                <div>
                  <label htmlFor="a-lname" className={labelClass}>
                    {SIGNUP_LABELS.lname}
                  </label>
                  <input
                    id="a-lname"
                    autoComplete="family-name"
                    disabled={submitting}
                    className={well}
                    {...register("lname")}
                  />
                  <FieldError message={errors.lname?.message} />
                </div>
              </div>

              <div>
                <label htmlFor="a-email" className={labelClass}>
                  {SIGNUP_LABELS.email}
                </label>
                <input
                  id="a-email"
                  type="email"
                  autoComplete="email"
                  disabled={submitting}
                  className={well}
                  {...register("email")}
                />
                <FieldError message={errors.email?.message} />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="a-username" className={labelClass}>
                    {SIGNUP_LABELS.username}
                  </label>
                  <input
                    id="a-username"
                    autoComplete="username"
                    disabled={submitting}
                    className={well}
                    {...register("username")}
                  />
                  <FieldError message={errors.username?.message} />
                </div>
                <div>
                  <label htmlFor="a-phone" className={labelClass}>
                    {SIGNUP_LABELS.phone}
                  </label>
                  <input
                    id="a-phone"
                    type="tel"
                    autoComplete="tel"
                    disabled={submitting}
                    className={well}
                    {...register("phone")}
                  />
                  <FieldError message={errors.phone?.message} />
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="a-password" className={labelClass}>
                    {SIGNUP_LABELS.password}
                  </label>
                  <input
                    id="a-password"
                    type="password"
                    autoComplete="new-password"
                    disabled={submitting}
                    className={well}
                    {...register("password")}
                  />
                  <FieldError message={errors.password?.message} />
                </div>
                <div>
                  <label htmlFor="a-confirmpassword" className={labelClass}>
                    {SIGNUP_LABELS.confirmpassword}
                  </label>
                  <input
                    id="a-confirmpassword"
                    type="password"
                    autoComplete="new-password"
                    disabled={submitting}
                    className={well}
                    {...register("confirmpassword")}
                  />
                  <FieldError message={errors.confirmpassword?.message} />
                </div>
              </div>

              <div>
                <label htmlFor="a-country" className={labelClass}>
                  {SIGNUP_LABELS.country_id}
                </label>
                <select
                  id="a-country"
                  disabled={submitting}
                  className={cn(well, "cursor-pointer appearance-none pr-10")}
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='1.75' stroke-linecap='round'><path d='M6 8l4 4 4-4'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "1.15rem",
                  }}
                  {...register("country_id")}
                >
                  <CountryOptions />
                </select>
                <FieldError message={errors.country_id?.message} />
              </div>

              <div>
                <label htmlFor="a-captcha" className={labelClass}>
                  {SIGNUP_LABELS.captcha}
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <CaptchaImage
                    src={captchaImage}
                    onRefresh={refreshCaptcha}
                    disabled={submitting}
                    imageClassName="rounded-xl border border-hairline"
                    buttonClassName="rounded-lg border border-hairline hover:border-border"
                  />
                  <input
                    id="a-captcha"
                    autoComplete="off"
                    disabled={submitting}
                    className={cn(well, "min-w-[10rem] flex-1")}
                    {...register("captcha")}
                  />
                </div>
                <FieldError message={errors.captcha?.message} />
              </div>

              <div className="space-y-3 pt-1">
                <CheckRow
                  id="a-terms"
                  label={SIGNUP_TERMS_LABEL}
                  disabled={submitting}
                  registration={register("terms")}
                />
                <FieldError message={errors.terms?.message} className="mt-0" />
                <CheckRow
                  id="a-newsletter"
                  label={SIGNUP_NEWSLETTER_LABEL}
                  disabled={submitting}
                  registration={register("newsletter")}
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={submitting}
                  className={cn(
                    "mt-2 h-12 w-full rounded-lg bg-primary text-sm font-semibold text-primary-foreground",
                    "transition-colors duration-150 hover:bg-primary active:bg-primary",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    "disabled:cursor-not-allowed disabled:bg-primary/45",
                  )}
                >
                  {submitting ? SIGNUP_SUBMITTING : SIGNUP_SUBMIT}
                </button>
                <ProgressSweep
                  active={submitting}
                  className="mt-3 rounded-full"
                  barClassName="via-primary"
                />
                <span role="status" aria-live="polite" className="sr-only">
                  {submitting ? "Creating your account" : ""}
                </span>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
